import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IError } from "toolkit/profile/types";
import {
	changeActiveFilter,
	getRoles,
	getUserRoles,
	getUsers,
	setAdmin,
	setRole,
	signUpUser,
	unsetAdmin,
} from "./users.thunks";
import { IUser } from "./users.types";
import { RolesList } from "libs/apiClient/data-contracts";

interface UsersState {
	usersList: IUser[];
	usersFilters: {
		position: any[];
		role: any[];
		status: any[];
	};
	currentUser: { admin: boolean; organizations: any[] };
	roles: RolesList;
	error: IError;
}

const initialState: UsersState = {
	usersList: [],
	usersFilters: { position: [], role: [], status: [] },
	currentUser: { admin: false, organizations: [] },
	roles: {
		user: [],
		system: [],
	},
	error: {
		status: null,
		message: "",
	},
};

const usersSlice = createSlice({
	name: "usersSlice",
	initialState,
	reducers: {
		ClearError(state) {
			state.error = {
				status: null,
				message: "",
			};
		},
		setUsers(state, action: PayloadAction<{ data: any; change: boolean; filterData?: boolean }>) {
			const { change, data, filterData } = action.payload;
			if (change) {
				state.usersList = data.list;
				if (filterData) {
					let position = [
						{
							id: 0,
							name: "По умолчанию",
							column: "position",
							active: true,
						},
						...Object.values(data.possibleFilters.position as { [key: string]: any }).map(
							(_: string, index: number) => ({
								column: "position",
								active: false,
								name: _,
								id: index + 1,
							}),
						),
					];
					let status = [
						{
							id: 0,
							name: "По умолчанию",
							column: "status",
							active: true,
						},
						...Object.values(data.possibleFilters.status as { [key: string]: any }).map((_: string, index: number) => ({
							column: "status",
							active: false,
							name: _,
							id: index + 1,
						})),
					];
					let role = [
						{
							id: 0,
							name: "По умолчанию",
							column: "role",
							active: true,
						},
						...Object.values(data.possibleFilters.role as { [key: string]: any }).map((_: string, index: number) => ({
							column: "role",
							active: false,
							name: _,
							id: index + 1,
						})),
					];
					state.usersFilters = {
						role,
						status,
						position,
					};
				}
			} else {
				state.usersList = [...state.usersList, ...data.list];
			}
		},
	},
	extraReducers: (builder) => {
		builder.addCase(getUsers.fulfilled, (state, action) => {
			const { possibleFilters, list, change } = action.payload;

			state.usersList = list;

			let position: any[] = [],
				role: any[] = [],
				status: any[] = [];
			if (change) {
				position = [
					{
						id: 0,
						name: "По умолчанию",
						column: "position",
						active: true,
					},
					...Object.values(possibleFilters.position as { [key: string]: any }).map((_: string, index: number) => ({
						column: "position",
						active: false,
						name: _,
						id: index + 1,
					})),
				];
				status = [
					{
						id: 0,
						name: "По умолчанию",
						column: "status",
						active: true,
					},
					...Object.values(possibleFilters.status as { [key: string]: any }).map((_: string, index: number) => ({
						column: "status",
						active: false,
						name: _,
						id: index + 1,
					})),
				];
				role = [
					{
						id: 0,
						name: "По умолчанию",
						column: "role",
						active: true,
					},
					...Object.values(possibleFilters.role as { [key: string]: any }).map((_: string, index: number) => ({
						column: "role",
						active: false,
						name: _,
						id: index + 1,
					})),
				];
				state.usersFilters = {
					role,
					status,
					position,
				};
			}
		});

		builder.addCase(signUpUser.fulfilled, (state, action) => {
			state.error = {
				status: null,
				message: "",
			};
			state.usersList.push(action.payload);
		});

		builder.addCase(signUpUser.rejected, (state, action) => {
			state.error = action.payload as any;
		});

		builder.addCase(changeActiveFilter.fulfilled, (state, action) => {
			state.usersFilters = action.payload;
		});

		builder.addCase(getUserRoles.fulfilled, (state, action: PayloadAction<any>) => {
			state.currentUser.admin = action.payload.admin;
			state.currentUser.organizations = action.payload.roles;
		});

		builder.addCase(setAdmin.fulfilled, (state) => {
			state.currentUser.admin = true;
		});

		builder.addCase(unsetAdmin.fulfilled, (state) => {
			state.currentUser.admin = false;
		});

		builder.addCase(getRoles.fulfilled, (state, action) => {
			state.roles = action.payload;
		});

		builder.addCase(setRole.fulfilled, (state, action) => {
			const { organizationId, role } = action.payload;

			const organizationsIds = state.currentUser.organizations.map((org) => org.id);

			const candidateIndex = organizationsIds.indexOf(organizationId);

			if (candidateIndex !== -1) {
				state.currentUser.organizations[candidateIndex].role = {
					id: organizationId,
					name: role.name,
				};
			}
		});
	},
});

export const { ClearError, setUsers } = usersSlice.actions;
export default usersSlice.reducer;
